/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { Button, Link, Select, TextField } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadSystemList from "@monsma/App/Handlers/CommonHandlers/LoadSystemList";
import { xAppState } from "@monsma/App/Store/Reducers";
import Notify from "@monsma/XDL/Components/Notify/Notify";
import { screen } from "@monsma/XDL/Helpers";
import * as React from "react";
import { FC } from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: xAppState) => {
    return {
        systemList: state.xSystemList,
    };
};

const mapDispatchToProps = {
    loadSystemList,
};

const systemListsNames =
    "Custom Resource System List 1,Custom Resource System List 2,Custom Resource System List 3,Custom Resource System List 4";

const initalFilterValues = {
    fileDisplayName: "",
    brandDropDown: "",
    categoryDropDown: "",
    customResource3: "",
    customResource4: "",
};

const enum fields {
    resources = "resources",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.resources]: ResourceModel[];
    };
}

interface ResourceModel {
    fields: {
        fileDisplayName: string;
        pdfFileLink: string;
        brandDropDown: string;
        categoryDropDown: string;
        customResource3: string;
        customResource4: string;
    };
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ResourceLinkStyles {
    title?: InjectableCss;
    resourceTitle: LinkPresentationProps;
    clearFilterButtonWrapper: GridItemProps;
    filtersContainer?: GridContainerProps;
    gridItem?: GridItemProps;
}

export const resourceLinkStyles: ResourceLinkStyles = {
    title: {
        css: css`
            font-size: 22px;
            font-weight: 700;
            margin-bottom: 16px;
            color: "text.main";
        `,
    },
    resourceTitle: {
        color: "text",
        hoverStyle: {
            color: "text",
        },
        typographyProps: {
            variant: "h2",
            size: 15,
            lineHeight: "22px",
            weight: "normal",
        },
        css: css`
            margin-bottom: 12px;
            text-decoration: underline;
            display: block;
        `,
    },
    filtersContainer: {
        css: css`
            &&& {
                margin-bottom: 40px;
            }
        `,
    },
    gridItem: {
        width: [12, 6, 6, 4, 4],
        css: css`
            padding-bottom: 30px;
        `,
    },
    clearFilterButtonWrapper: {
        width: [12, 6, 6, 4, 4],
        css: css`
            display: flex;
            align-items: center;
        `,
    },
};

const styles = resourceLinkStyles;

type FilterKeys = "fileDisplayName" | "brandDropDown" | "categoryDropDown" | "customResource3" | "customResource4";
type Filter = {
    [key in FilterKeys]: string;
};

type SortDir = "AZ" | "ZA" | "";
interface FilterProps {
    onSort: (sortDir: SortDir) => void;
    onFilterChange: (filter: Filter) => void;
    onSearch: (query: string) => void;
    onClear: () => void;
}

const ResourcesFilter: FC<FilterProps & Pick<Props, "systemList">> = ({
    onSort,
    onFilterChange,
    systemList,
    onClear,
    onSearch,
}) => {
    const [filter, setFilter] = useState<Filter>(initalFilterValues);
    const [sortBy, setSortBy] = useState<SortDir>("");
    const [query, setQuery] = useState<string>("");
    const handleFilterChange = (key: FilterKeys) => (event: any) => {
        const newFilter = { ...filter, [key]: event.target.value };
        setFilter(newFilter);
        onFilterChange(newFilter);
    };
    const handleSort = (event: any) => {
        const sortDir = event.target.value;
        setSortBy(sortDir);
        onSort(sortDir);
    };
    const handleSearch = (event: any) => {
        const query = event.target.value;
        setQuery(query);
        onSearch(query);
    };

    const handleClear = () => {
        setFilter(initalFilterValues);
        setQuery("");
        setSortBy("");
        onClear();
    };

    return (
        <GridContainer {...styles.filtersContainer}>
            {systemList["Custom Resource System List 1"] && (
                <GridItem {...styles.gridItem}>
                    <Select onChange={handleFilterChange("brandDropDown")} label="Filter By Brand"
                        value={filter["brandDropDown"]}>
                        <option value="">{translate("Show All")}</option>
                        {systemList["Custom Resource System List 1"].values.map(i => (
                            <option key={i.name} value={i.name}>
                                {translate(`${i.name}`)}
                            </option>
                        ))}
                    </Select>
                </GridItem>
            )}
            {systemList["Custom Resource System List 2"] && (
                <GridItem {...styles.gridItem}>
                    <Select
                        onChange={handleFilterChange("categoryDropDown")}
                        label="Filter By Category"
                        value={filter["categoryDropDown"]}
                    >
                        <option value="">{translate("Show All")}</option>
                        {systemList["Custom Resource System List 2"].values.map(i => (
                            <option key={i.name} value={i.name}>
                                {translate(`${i.name}`)}
                            </option>
                        ))}
                    </Select>
                </GridItem>
            )}
            {systemList["Custom Resource System List 3"] && (
                <GridItem {...styles.gridItem}>
                    <Select
                        onChange={handleFilterChange("customResource3")}
                        label={systemList["Custom Resource System List 3"].properties["custom Resource System List 3"] ? systemList["Custom Resource System List 3"].properties["custom Resource System List 3"] : "Filter By Resource System List #3"}
                        value={filter["customResource3"]}
                    >
                        <option value="">{translate("Show All")}</option>
                        {systemList["Custom Resource System List 3"].values.map(i => (
                            <option key={i.name} value={i.name}>
                                {translate(`${i.name}`)}
                            </option>
                        ))}
                    </Select>
                </GridItem>
            )}            
            {systemList["Custom Resource System List 4"] && (
                <GridItem {...styles.gridItem}>
                    <Select
                        onChange={handleFilterChange("customResource4")}
                        label={systemList["Custom Resource System List 4"].properties["custom Resource System List 4"] ? systemList["Custom Resource System List 4"].properties["custom Resource System List 4"] : "Filter By Resource System List #4"}
                        value={filter["customResource4"]}
                    >
                        <option value="">{translate("Show All")}</option>
                        {systemList["Custom Resource System List 4"].values.map(i => (
                            <option key={i.name} value={i.name}>
                                {translate(`${i.name}`)}
                            </option>
                        ))}
                    </Select>
                </GridItem>
            )}

            <GridItem {...styles.gridItem}>
                <TextField
                    label="Search By File Display Name"
                    value={query}
                    data-test-selector="searchByFileName"
                    onChange={handleSearch}
                />
            </GridItem>
            <GridItem {...styles.gridItem}>
                <Select onChange={handleSort} label="Sort By" value={sortBy}>
                    <option value="">{translate("Sort By")}</option>
                    <option value="AZ">{translate("A to Z")}</option>
                    <option value="ZA">{translate("Z to A")}</option>
                </Select>
            </GridItem>
            <GridItem {...styles.clearFilterButtonWrapper}>
                <Button onClick={handleClear} sizeVariant="medium">
                    {translate("Clear Filters")}
                </Button>
            </GridItem>
        </GridContainer>
    );
};

const ResourceLinkWidget: FC<Props> = ({ fields, loadSystemList, systemList }) => {
    React.useEffect(() => {
        loadSystemList({ systemListsNames });
    }, []);
    const [resources, setResources] = React.useState(fields.resources);

    const handleFilterResources = (filter: Filter) => {
        const filtered = fields.resources.filter(resource => {
            return (
                (!filter.brandDropDown || resource.fields.brandDropDown === filter.brandDropDown) &&
                (!filter.categoryDropDown || resource.fields.categoryDropDown === filter.categoryDropDown) &&
                (!filter.customResource3 || resource.fields.customResource3 === filter.customResource3) &&
                (!filter.customResource4 || resource.fields.customResource4 === filter.customResource4)
            );
        });
        setResources(filtered);
    };

    const handleSearch = (query: string) => {
        if (!query) {
            setResources(fields.resources);
            return;
        }
        const filtered = resources.filter(
            resource => resource.fields.fileDisplayName.toLowerCase().indexOf(query.toLowerCase()) > -1,
        );

        setResources(filtered);
    };

    const handleSort = (sortDir: SortDir) => {
        if (!sortDir) {
            return;
        }
        const sorted = [...resources].sort((a, b) => {
            if (sortDir === "AZ") {
                return a.fields.fileDisplayName.localeCompare(b.fields.fileDisplayName);
            } 
            return b.fields.fileDisplayName.localeCompare(a.fields.fileDisplayName);            
        });
        setResources(sorted);
    };

    const handleClear = () => {
        setResources(fields.resources);
    };
    return (
        <StyledWrapper id="resourceLinkWidget">
            <ResourcesFilter
                systemList={systemList}
                onSearch={handleSearch}
                onFilterChange={handleFilterResources}
                onSort={handleSort}
                onClear={handleClear}
            />
            <StyledWrapper>
                {resources?.length > 0 && (
                    <StyledWrapper {...styles.title}>
                        <Typography as="h2">{translate("Resources")}</Typography>
                    </StyledWrapper>
                )}
                {resources?.length > 0 &&
                    resources.map((resource, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Link target="_blank" key={index} href={resource.fields.pdfFileLink} {...styles.resourceTitle}>
                            {resource.fields.fileDisplayName}
                        </Link>
                    ))}
                {resources?.length === 0 && (
                    <StyledWrapper>
                        <Notify messageType="danger" body={siteMessage("Resources_NoResultsFound")} hideCloseButton />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.resources,
            displayName: "Resources Links",
            editorTemplate: "ListField",
            getDisplay: (item: HasFields) => {
                return item.fields.fileDisplayName;
            },
            defaultValue: [],
            fieldType: "General",
            fieldDefinitions: [
                {
                    name: "fileDisplayName",
                    displayName: "File Display Name",
                    editorTemplate: "TextField",
                    defaultValue: "",
                    placeholder: "File Display Name",
                    isRequired: true,
                },
                {
                    name: "pdfFileLink",
                    displayName: "PDF file link",
                    fieldType: "General",
                    editorTemplate: "CustomFilePickerField",
                    isRequired: true,
                    defaultValue: "",
                    ...({ isFile: true } as any),
                },
                {
                    name: "brandDropDown",
                    displayName: "Brand Dropdown",
                    editorTemplate: "SystemListDropDownField",
                    systemListName: "Custom Resource System List 1",
                    defaultValue: "",
                    isRequired: true,
                },
                {
                    name: "categoryDropDown",
                    displayName: "Category Dropdown",
                    editorTemplate: "SystemListDropDownField",
                    systemListName: "Custom Resource System List 2",
                    defaultValue: "",
                    isRequired: true,
                },
                {
                    name: "customResource3",
                    displayName: "Custom Resource System List #3",
                    editorTemplate: "SystemListDropDownField",
                    systemListName: "Custom Resource System List 3",
                    defaultValue: "",
                    isRequired: true,
                },
                {
                    name: "customResource4",
                    displayName: "Custom Resource System List #4",
                    editorTemplate: "SystemListDropDownField",
                    systemListName: "Custom Resource System List 4",
                    defaultValue: "",
                    isRequired: true,
                },
            ],
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ResourceLinkWidget),
    definition,
};

export default widgetModule;
